import { useState } from "react";
import { today, getLocalTimeZone } from "@internationalized/date";
import {
  DealComponentPaymentRequestDto,
  PaymentType,
} from "../../../../types/types";
import {
  findEventDateField,
  getDeadlineRules,
  getEventDate,
} from "../../../../utils/deals/dealFormStepHelpers";
import {
  PaymentDateErrors,
  PaymentDates,
  PaymentOffsets,
  PaymentWindows,
  buildPaymentWindows,
  getDefaultPaymentDates,
  getPaymentDateErrors,
  getPaymentOffsets,
} from "../../../../utils/deals/paymentWindows";
import { splitPayoutsByTiming } from "../../../../utils/deals/dealPayoutSummary";
import type { PayoutItem } from "../componentPaymentsStep";
import { DealFormData, TemplateStep } from "../dealFormTypes";

type Translate = (
  key: string,
  params?: Record<string, string | number>,
) => string;

type UseComponentPaymentsParams = {
  formData: DealFormData;
  templateSteps: TemplateStep[];
  savedDates: PaymentDates;
  payoutSummary: PayoutItem[];
  paymentType: PaymentType;
  t: Translate;
};

type UseComponentPaymentsResult = {
  dates: PaymentDates;
  setPreDate: (date: string) => void;
  setPostDate: (date: string) => void;
  windows: PaymentWindows | null;
  offsets: PaymentOffsets | null;
  errors: PaymentDateErrors;
  hasErrors: boolean;
  preMatchPayouts: PayoutItem[];
  postMatchPayouts: PayoutItem[];
  buildPayments: () => DealComponentPaymentRequestDto[];
};

// State for the component payments step. The dates start as the saved ones (edit)
// or as the template defaults, and follow the event date until the user picks one.
export function useComponentPayments({
  formData,
  templateSteps,
  savedDates,
  payoutSummary,
  paymentType,
  t,
}: UseComponentPaymentsParams): UseComponentPaymentsResult {
  const [chosen, setChosen] = useState<Partial<PaymentDates>>({});

  const eventDate = getEventDate(formData, templateSteps);
  const offsets = getPaymentOffsets(getDeadlineRules(templateSteps));
  const windows = buildPaymentWindows(
    eventDate,
    offsets,
    today(getLocalTimeZone()).toString(),
  );
  const defaults = getDefaultPaymentDates(windows);

  const dates: PaymentDates = {
    pre: chosen.pre ?? (savedDates.pre || defaults.pre),
    post: chosen.post ?? (savedDates.post || defaults.post),
  };

  const { preMatchPayouts, postMatchPayouts } = splitPayoutsByTiming(
    payoutSummary,
    paymentType,
  );

  const errors = getPaymentDateErrors(
    {
      windows,
      dates,
      paymentType,
      hasPrePayouts: preMatchPayouts.length > 0,
      hasPostPayouts: postMatchPayouts.length > 0,
    },
    t,
  );

  const buildPayments = (): DealComponentPaymentRequestDto[] => {
    const payments: DealComponentPaymentRequestDto[] = [];
    if (dates.pre) {
      payments.push({ paymentTiming: "pre_event", dueOn: dates.pre });
    }
    if (paymentType === PaymentType.SPLIT && dates.post) {
      payments.push({ paymentTiming: "post_event", dueOn: dates.post });
    }
    return payments;
  };

  return {
    dates,
    setPreDate: (date) => setChosen((prev) => ({ ...prev, pre: date })),
    setPostDate: (date) => setChosen((prev) => ({ ...prev, post: date })),
    windows,
    offsets,
    errors,
    hasErrors: Boolean(errors.pre || errors.post),
    preMatchPayouts,
    postMatchPayouts,
    buildPayments,
  };
}
